import React, { useEffect, useRef, useState } from 'react';
import { useT } from '../../i18n/LanguageContext';
import useShippingRequest from '../../hooks/useShippingRequest';
import distributorService from '../../services/distributorService';
import CashDepositReview from './CashDepositReview';
import { readAdminCashDeposits } from './cashReviewContract';
import { CASH_DEPOSIT_METHODS, cashDepositMethod, depositStateLabel } from './cashDepositContract';
import { usd } from './cashMoney';
export default function CashAdminDeposits({
  distributorId,
  disabled,
  onLockChange,
  onFundsChanged,
}) {
  const t = useT();
  const [history, setHistory] = useState(null),
    [page, setPage] = useState(1),
    [selected, setSelected] = useState(null);
  const [error, setError] = useState(''),
    [retry, setRetry] = useState(0),
    [available, setAvailable] = useState(true);
  const callback = useRef(onLockChange);
  callback.current = onLockChange;
  const fundsCallback = useRef(onFundsChanged),
    credited = useRef(new Set());
  fundsCallback.current = onFundsChanged;
  const { begin, version } = useShippingRequest(`admin-cash-deposits:${distributorId}:${page}`);
  useEffect(() => {
    callback.current?.(Boolean(selected));
    return () => callback.current?.(false);
  }, [selected]);
  useEffect(() => {
    const request = begin();
    setHistory(null);
    setError('');
    setAvailable(true);
    const loading = distributorService.adminCashDeposits(distributorId, page, {
      signal: request.signal,
    });
    if (!loading) {
      setAvailable(false);
      return;
    }
    Promise.resolve(loading)
      .then((response) => {
        if (request.isCurrent()) setHistory(readAdminCashDeposits(response?.data?.data, page));
      })
      .catch((failure) => {
        if (request.isCurrent()) {
          if (failure?.response?.status === 404) setAvailable(false);
          else setError('Cash funding history could not load. Reload before reviewing.');
        }
      });
  }, [begin, version, distributorId, page, retry]);
  if (!available) return null;
  return (
    <fieldset className="me-cash me-cash-card">
      <legend>{t('Cash funding requests')}</legend>
      <div className="me-cash-heading">
        <h2>{t('Funding & payment status')}</h2>
        <button
          type="button"
          disabled={disabled || Boolean(selected)}
          onClick={() => setRetry((value) => value + 1)}
        >
          {t('Refresh requests')}
        </button>
      </div>
      {error && <p role="alert">{t(error)}</p>}
      {!history && !error && <p role="status">{t('Loading cash funding requests…')}</p>}
      {history && !history.items.length && <p>{t('No cash funding requests yet.')}</p>}
      {history?.items.length > 0 && (
        <div className="me-cash-scroll">
          <table>
            <caption className="sr-only">{t('Cash funding requests')}</caption>
            <thead>
              <tr>
                <th scope="col">{t('Date')}</th>
                <th scope="col">{t('Purchasing rep')}</th>
                <th scope="col">{t('Method')}</th>
                <th scope="col">{t('Status')}</th>
                <th scope="col">{t('Amount')}</th>
                <th scope="col">{t('Review')}</th>
              </tr>
            </thead>
            <tbody>
              {history.items.map((row) => (
                <tr key={row._id}>
                  <td>{new Date(row.createdAt).toLocaleString()}</td>
                  <td>
                    {row.actor.name || row.actor.username}
                    <small>{row.actor.email}</small>
                  </td>
                  <td>{t(CASH_DEPOSIT_METHODS[cashDepositMethod(row)])}</td>
                  <td>{t(depositStateLabel(row))}</td>
                  <td>{usd(row.amountCents)}</td>
                  <td>
                    <button
                      type="button"
                      disabled={disabled || Boolean(selected)}
                      onClick={() => setSelected(row)}
                    >
                      {t('Review funding')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {history && history.total > 10 && (
        <div className="me-cash-actions">
          <button
            type="button"
            disabled={disabled || Boolean(selected) || page === 1}
            onClick={() => setPage(page - 1)}
          >
            {t('Previous')}
          </button>
          <span>{t('Page {page}', { page })}</span>
          <button
            type="button"
            disabled={disabled || Boolean(selected) || page * 10 >= history.total}
            onClick={() => setPage(page + 1)}
          >
            {t('Next')}
          </button>
        </div>
      )}
      {selected && (
        <CashDepositReview
          distributorId={distributorId}
          deposit={selected}
          onClose={() => setSelected(null)}
          onUpdated={(row) => {
            setRetry((value) => value + 1);
            if (row?.status === 'credited' && !credited.current.has(row._id)) {
              credited.current.add(row._id);
              fundsCallback.current?.();
            }
          }}
        />
      )}
    </fieldset>
  );
}
